import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes } from 'react-icons/fa';
import { Icon } from '../../components/Icon';
import { api } from '../../api/client';
import './Navbar.css';

interface NavBarProps {
	isNavbarVisible: boolean;
}

const NavBar: React.FC<NavBarProps> = ({ isNavbarVisible }) => {
	const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const location = useLocation();

	useEffect(() => {
		const handleResize = () => {
			setIsDesktop(window.innerWidth > 768);
			if (window.innerWidth > 768) {
				setIsMobileMenuOpen(false);
			}
		};
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	// Re-check auth whenever the route changes, so logging in/out on /admin
	// flips the Morse link on/off without a full page reload.
	useEffect(() => {
		let cancelled = false;
		api.auth.me()
			.then((r) => { if (!cancelled) setIsAuthenticated(!!r.authenticated); })
			.catch(() => { if (!cancelled) setIsAuthenticated(false); });
		return () => { cancelled = true; };
	}, [location.pathname]);

	const inAdminContext =
		location.pathname.startsWith('/admin') || location.pathname.startsWith('/morse');

	const links = [
		{ url: '/',  label: 'Works' },
		{ url: '/live',  label: 'Live' },
		{ url: '/about',  label: 'About' },
		...(isAuthenticated && inAdminContext ? [{ url: '/morse', label: 'Morse' }] : []),
	];

	const toggleMobileMenu = () => {
		setIsMobileMenuOpen(!isMobileMenuOpen);
	};

	const closeMobileMenu = () => {
		setIsMobileMenuOpen(false);
	};

	if (!isNavbarVisible) return null;

	return (
		<nav className="navbar-container">
			<motion.div
				className="navbar-content"
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ delay: 0.5 }}
			>
				{/* Brand */}
				<Link to="/" className="brand-section nav-link" onClick={closeMobileMenu}>
					<span className="navbar-brand">LORENZO SAINI | LO.SAI</span>
				</Link>

				{/* Desktop Navigation */}
				{isDesktop && (
					<div className="desktop-navigation">
						{links.map(({ url, label }) => {
							const isActive = url === '/'
								? (location.pathname === '/' || location.pathname === '/works')
								: location.pathname === url;
							return (
								<Link
									key={url}
									to={url}
									className={`nav-link${isActive ? ' active' : ''}`}
								>
									<span className="nav-label">{label}</span>
								</Link>
							);
						})}
					</div>
				)}

				{/* Mobile Hamburger Button */}
				{!isDesktop && (
					<button
						className={`mobile-menu-button ${isMobileMenuOpen ? 'open' : ''}`}
						onClick={toggleMobileMenu}
						aria-label="Toggle mobile menu"
					>
						{isMobileMenuOpen ? <Icon icon={FaTimes} /> : <Icon icon={FaBars} />}
					</button>
				)}
			</motion.div>

			{/* Mobile Navigation Menu Overlay */}
			<AnimatePresence>
{!isDesktop && isMobileMenuOpen && (
					<motion.div
						className="mobile-navigation-overlay"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.2 }}
						onClick={closeMobileMenu}
					>
						<Link to="/" className="overlay-logo-link" onClick={closeMobileMenu}>
							<span className="navbar-brand overlay-brand">LORENZO SAINI | LO.SAI</span>
						</Link>
						<motion.div
							className="mobile-navigation"
							initial={{ y: -20, opacity: 0 }}
							animate={{ y: 0, opacity: 1 }}
							exit={{ y: -20, opacity: 0 }}
							transition={{ duration: 0.2 }}
							onClick={(e: any) => e.stopPropagation()}
						>
							{links.map(({ url, label }) => {
								const isActive = url === '/'
									? (location.pathname === '/' || location.pathname === '/works')
									: location.pathname === url;
								return (
									<Link
										key={url}
										to={url}
										className={`mobile-nav-link${isActive ? ' active' : ''}`}
										onClick={closeMobileMenu}
									>
										<span className="mobile-nav-label">{label}</span>
									</Link>
								);
							})}
						</motion.div>
					</motion.div>
				)}
			</AnimatePresence>
		</nav>
	);
};

export default NavBar;